/**
 * Simple Throttled Presence Manager - JavaScript Implementation
 * Manages presence tracking for Matrix users with throttled updates
 */

import { UserEvent } from 'matrix-js-sdk';
import logger from '../utils/logger';

// Simple throttle implementation (avoiding lodash dependency)
const throttle = (func, delay) => {
  let timeoutId;
  let lastExecTime = 0;
  let cancelled = false;
  
  const throttledFunction = (...args) => {
    const currentTime = Date.now();
    
    if (cancelled) return;
    
    if (currentTime - lastExecTime > delay) {
      // Execute immediately if enough time has passed
      lastExecTime = currentTime;
      func.apply(this, args);
    } else {
      // Schedule execution for later
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (!cancelled) {
          lastExecTime = Date.now();
          func.apply(this, args);
        }
      }, delay - (currentTime - lastExecTime));
    }
  };
  
  throttledFunction.cancel = () => {
    cancelled = true;
    clearTimeout(timeoutId);
  };
  
  return throttledFunction;
};

class PresenceManager {
  constructor(matrixClient) {
    this.client = matrixClient;
    
    // Core data structures
    this.presenceCache = new Map(); // userId -> presence data
    this.trackedUsers = new Set();  // Set of tracked user IDs
    this.callbacks = new Set();     // Set of callback functions
    this.pendingUsers = new Set();  // Users with pending updates
    
    // Throttled update function with 500ms delay
    this.throttledUpdate = throttle(() => {
      this.flush();
    }, 500);
    
    // Bind event listeners
    this.handlePresenceEvent = this.handlePresenceEvent.bind(this);
    this.handleCurrentlyActiveEvent = this.handleCurrentlyActiveEvent.bind(this);
    this.handleOwnPresenceChanged = this.handleOwnPresenceChanged.bind(this);

    // Setup event listeners
    if (this.client) {
      this.client.on(UserEvent.Presence, this.handlePresenceEvent);
      this.client.on(UserEvent.CurrentlyActive, this.handleCurrentlyActiveEvent);
      logger.debug('PresenceManager: Event listeners attached');
    }

    // Listen for own presence changes from matrixService.setUserPresence()
    window.addEventListener('own-presence-changed', this.handleOwnPresenceChanged);
  }
  
  /**
   * Handle presence change events from Matrix
   */
  handlePresenceEvent(event, user) {
    const userId = user.userId;
    
    if (this.trackedUsers.has(userId)) {
      logger.debug(`PresenceManager: Presence event for ${userId}:`, event.getContent());
      this.updatePresence(userId, user);
    }
  }
  
  /**
   * Handle currently active events from Matrix
   */
  handleCurrentlyActiveEvent(event, user) {
    const userId = user.userId;

    if (this.trackedUsers.has(userId)) {
      logger.debug(`PresenceManager: Currently active event for ${userId}`);
      this.updatePresence(userId, user);
    }
  }

  /**
   * Handle own presence changed event from matrixService.setUserPresence()
   * This ensures the local UI updates immediately when the user changes their presence setting
   */
  handleOwnPresenceChanged(event) {
    const { userId, presence, statusMsg } = event.detail;

    if (userId && this.trackedUsers.has(userId)) {
      logger.debug(`PresenceManager: Own presence changed to ${presence}`);
      this.updatePresence(userId, {
        userId: userId,
        presence: presence,
        currentlyActive: presence === 'online',
        lastActiveAgo: 0,
        presenceStatusMsg: statusMsg,
      });
    }
  }
  
  /**
   * Update presence data for a user and trigger throttled callback
   */
  updatePresence(userId, user) {
    const presenceData = {
      userId: userId,
      presence: user.presence || 'offline',
      lastActiveAgo: user.lastActiveAgo || null,
      currentlyActive: user.currentlyActive || false,
      statusMsg: user.presenceStatusMsg || '',
      timestamp: Date.now(),
    };
    
    this.presenceCache.set(userId, presenceData);
    this.pendingUsers.add(userId);
    
    // Trigger throttled update
    this.throttledUpdate();
  }
  
  /**
   * Add a user to tracking - relies on /sync events for presence updates
   */
  trackUser(userId) {
    if (!userId) return;

    logger.debug(`PresenceManager: Starting to track user ${userId}`);
    this.trackedUsers.add(userId);

    // Only use SDK cache, never make HTTP calls
    // Presence updates will come via /sync events for users we share rooms with
    if (this.client) {
      const user = this.client.getUser(userId);

      if (user?.presence && user.presence !== 'unknown') {
        // Found in Matrix SDK's memory cache
        logger.debug(`PresenceManager: Found cached presence for ${userId}: ${user.presence}`);
        this.updatePresence(userId, user);
      } else {
        // No cached presence - set as offline and wait for sync events
        // If we share a room with this user, presence will arrive via /sync
        // If we don't share a room, we won't have permission anyway
        logger.debug(`PresenceManager: No cached presence for ${userId}, waiting for sync events`);
        this.presenceCache.set(userId, {
          userId: userId,
          presence: 'offline',
          lastActiveAgo: null,
          currentlyActive: false,
          statusMsg: '',
          timestamp: Date.now(),
        });
      }
    }
  }
  
  /**
   * Remove a user from tracking
   */
  untrackUser(userId) {
    logger.debug(`PresenceManager: Stopping tracking for user ${userId}`);
    this.trackedUsers.delete(userId);
    this.presenceCache.delete(userId);
    this.pendingUsers.delete(userId);
  }
  
  /**
   * Subscribe to presence updates
   * Returns unsubscribe function
   */
  subscribe(callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }
    
    this.callbacks.add(callback);
    logger.debug('PresenceManager: Added callback subscriber');
    
    // Return unsubscribe function
    return () => {
      this.callbacks.delete(callback);
      logger.debug('PresenceManager: Removed callback subscriber');
    };
  }
  
  /**
   * Flush pending updates to all subscribers
   */
  flush() {
    if (this.pendingUsers.size === 0) return;
    
    const updatedData = new Map();
    this.pendingUsers.forEach(userId => {
      const presence = this.presenceCache.get(userId);
      if (presence) {
        updatedData.set(userId, presence);
      }
    });
    
    logger.debug(`PresenceManager: Flushing updates for ${updatedData.size} users`);
    
    // Call all callbacks with updated data
    this.callbacks.forEach(callback => {
      try {
        callback(updatedData);
      } catch (error) {
        logger.error('PresenceManager: Error in callback:', error);
      }
    });
    
    // Clear pending users
    this.pendingUsers.clear();
  }
  
  /**
   * Get cached presence data for a user
   */
  getPresence(userId) {
    return this.presenceCache.get(userId) || null;
  }
  
  /**
   * Check if a user is considered online
   */
  isOnline(userId) {
    const presence = this.getPresence(userId);
    if (!presence) return false;
    
    // Treat 'unknown' state optimistically as potentially online for UI purposes
    // This prevents showing everyone as offline initially
    if (presence.presence === 'unknown') {
      // Check if we have any activity indicators
      return presence.currentlyActive === true;
    }
    
    return presence.presence === 'online' || presence.currentlyActive === true;
  }
  
  /**
   * Force refresh presence data for all tracked users from SDK cache
   * Does not make HTTP calls - relies on /sync events for updates
   */
  refreshAllPresence() {
    if (!this.client) return;

    logger.debug(`PresenceManager: Refreshing presence for ${this.trackedUsers.size} tracked users`);

    this.trackedUsers.forEach(userId => {
      const user = this.client.getUser(userId);

      if (user?.presence && user.presence !== 'unknown') {
        // Have valid cached data from SDK
        logger.debug(`PresenceManager: Refreshed presence for ${userId}: ${user.presence}`);
        this.updatePresence(userId, user);
      } else {
        // No valid cache - keep existing state, wait for sync events
        logger.debug(`PresenceManager: No cached presence for ${userId}, waiting for sync`);
      }
    });
  }
  
  /**
   * Set our own presence state on the Matrix server
   */
  async setOwnPresence(presence = 'online', statusMsg = '') {
    if (!this.client) return;
    
    try {
      logger.debug(`PresenceManager: Setting own presence to ${presence}`);
      await this.client.setPresence({
        presence: presence,
        status_msg: statusMsg,
      });
      
      // Also update our local cache immediately
      const userId = this.client.getUserId();
      if (userId) {
        this.updatePresence(userId, {
          userId: userId,
          presence: presence,
          currentlyActive: presence === 'online',
          lastActiveAgo: 0,
          statusMsg: statusMsg,
          getAvatarUrl: () => null,
        });
      }
    } catch (error) {
      logger.error('PresenceManager: Failed to set own presence:', error);
    }
  }

  /**
   * Get all tracked users' presence data for testing
   */
  getAllPresence() {
    const allPresence = {};
    this.presenceCache.forEach((presence, userId) => {
      allPresence[userId] = presence;
    });
    return allPresence;
  }
  
  /**
   * Cleanup and destroy the presence manager
   */
  destroy() {
    logger.debug('PresenceManager: Destroying presence manager');

    // Remove event listeners
    if (this.client) {
      this.client.removeListener(UserEvent.Presence, this.handlePresenceEvent);
      this.client.removeListener(UserEvent.CurrentlyActive, this.handleCurrentlyActiveEvent);
    }

    // Remove window event listener
    window.removeEventListener('own-presence-changed', this.handleOwnPresenceChanged);
    
    // Clear all data structures
    this.presenceCache.clear();
    this.trackedUsers.clear();
    this.callbacks.clear();
    this.pendingUsers.clear();
    
    // Cancel any pending throttled calls
    this.throttledUpdate.cancel();
  }
}

export default PresenceManager;